import Head from "next/head";
import type { ReactElement } from "react";
import Image from "next/image";

import { PageLayout } from "@/components/Layout";
import ProductsHeroSection from "@/components/ProductHeroSection";
import { usePathname } from "next/navigation";
import { env } from "@/config/env";

import ProductOfferingSection from "@/components/ProductOfferingSection";
import ProductKeyFeaturesSection from "@/components/ProductKeyFeaturesSection";
import ProductRevenueSection from "@/components/ProductRevenueSection";
import ProductUseCaseSection from "@/components/ProductUseCaseSection";
import ProductReasonSection from "@/components/ProductReasonSection";
import ProductCallToActionSection from "@/components/ProductCallToActionSection";
import ProductAPIIntegrationSection from "@/components/ProductAPIIntegrationSection";
import {
  PRODUCT_GASLESS_MINTING_INFORMATION,
  PRODUCT_GASLESS_MINTING_BRAND_ENGAGEMENT,
  PRODUCT_GASLESS_MINTING_CTA,
  PRODUCT_GASLESS_MINTING_INTEGRATION,
  PRODUCT_GASLESS_MINTING_KEY_FEATURES,
  PRODUCT_GASLESS_MINTING_OFFERING,
  PRODUCT_GASLESS_MINTING_REASON,
  PRODUCT_GASLESS_MINTING_USE_CASES,
} from "@/constants/products/gasless-minting";

const GaslessMintingPage = () => {
  const pathname = usePathname();
  const url = `https://${env.APP_DOMAIN}${pathname}`;
  const path = pathname.split("/")[2];
  return (
    <>
      <Head>
        <title>Mintology | {PRODUCT_GASLESS_MINTING_INFORMATION.title} </title>
        <meta
          name="description"
          content={PRODUCT_GASLESS_MINTING_INFORMATION.description}
        />
        <meta
          property="og:title"
          content={`${PRODUCT_GASLESS_MINTING_INFORMATION.title} | Mintology`}
        />
        <meta
          property="og:description"
          content={`${PRODUCT_GASLESS_MINTING_INFORMATION.description} | `}
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Mintology" />
        <meta
          property="og:image"
          content={PRODUCT_GASLESS_MINTING_INFORMATION.image}
        />
        <meta
          property="og:image:alt"
          content={PRODUCT_GASLESS_MINTING_INFORMATION.imageInfo}
        />
        <meta property="og:url" content={url} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={url} />
      </Head>
      <main className="pt-[4rem] px-7 lg:pt-[5rem] pb-12 container mx-auto flex flex-col gap-10 md:gap-28 lg:px-6 relative">
        <Image
          priority
          src={"/images/products/products-hero-background.png"}
          alt="Products Hero Background Image"
          height={300}
          width={500}
          className="object-contain absolute top-0 -z-10"
        />
        <ProductsHeroSection
          title={PRODUCT_GASLESS_MINTING_INFORMATION.title}
          description={PRODUCT_GASLESS_MINTING_INFORMATION.description}
          image={PRODUCT_GASLESS_MINTING_INFORMATION.image}
          highlight={PRODUCT_GASLESS_MINTING_INFORMATION.highlight}
          buttonText={PRODUCT_GASLESS_MINTING_INFORMATION.buttonText}
        />

        <ProductOfferingSection
          title={PRODUCT_GASLESS_MINTING_OFFERING.title}
          description={PRODUCT_GASLESS_MINTING_OFFERING.description}
          offerings={PRODUCT_GASLESS_MINTING_OFFERING.offerings}
          highlight={PRODUCT_GASLESS_MINTING_OFFERING.highlight}
        />

        <ProductKeyFeaturesSection
          keyFeatures={PRODUCT_GASLESS_MINTING_KEY_FEATURES.features}
          title={PRODUCT_GASLESS_MINTING_KEY_FEATURES.title}
          highlight={PRODUCT_GASLESS_MINTING_KEY_FEATURES.highlight}
          description={PRODUCT_GASLESS_MINTING_KEY_FEATURES.description}
          titleHeight={78}
        />

        <ProductAPIIntegrationSection
          title={PRODUCT_GASLESS_MINTING_INTEGRATION.title}
          highlight={PRODUCT_GASLESS_MINTING_INTEGRATION.highlight}
          description={PRODUCT_GASLESS_MINTING_INTEGRATION.description}
          path={path}
        />

        <ProductRevenueSection
          title={PRODUCT_GASLESS_MINTING_BRAND_ENGAGEMENT.title}
          highlight={PRODUCT_GASLESS_MINTING_BRAND_ENGAGEMENT.highlight}
          revenueStreams={
            PRODUCT_GASLESS_MINTING_BRAND_ENGAGEMENT.revenue_streams
          }
          titleHeight={PRODUCT_GASLESS_MINTING_BRAND_ENGAGEMENT.titleHeight}
        />

        <ProductUseCaseSection
          title={PRODUCT_GASLESS_MINTING_USE_CASES.title}
          highlight={PRODUCT_GASLESS_MINTING_USE_CASES.highlight}
          useCases={PRODUCT_GASLESS_MINTING_USE_CASES.useCases}
          image={PRODUCT_GASLESS_MINTING_USE_CASES.image}
        />

        <ProductReasonSection
          title={PRODUCT_GASLESS_MINTING_REASON.title}
          highlight={PRODUCT_GASLESS_MINTING_REASON.highlight}
          description={PRODUCT_GASLESS_MINTING_REASON.description}
          reasons={PRODUCT_GASLESS_MINTING_REASON.reasons}
        />

        <ProductCallToActionSection
          title={PRODUCT_GASLESS_MINTING_CTA.title}
          highlight={PRODUCT_GASLESS_MINTING_CTA.highlight}
          buttonText={PRODUCT_GASLESS_MINTING_CTA.buttonText}
        />
      </main>
    </>
  );
};

GaslessMintingPage.getLayout = (page: ReactElement) => {
  return <PageLayout>{page}</PageLayout>;
};

export default GaslessMintingPage;
